import React, { FC } from "react";
import { Typography, Box } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import { CategoryData } from "../types/dashboardTypes";
import { translateDashboardDistributionLabel } from "../utils/dashboardI18n";

interface DistributionPieChartProps {
  title?: string;
  data: CategoryData[];
  /** Shown when data is empty */
  emptyMessage?: string;
}

/** Pie for 2+ categories (shows all statuses in legend, including 0); single category shows a card. */
const DistributionPieChart: FC<DistributionPieChartProps> = ({
  title,
  data,
  emptyMessage
}) => {
  const { t } = useKivunovaTranslation();
  const resolvedEmpty =
    emptyMessage ?? t("dashboard-chart-empty", "No data to display");
  const dist = (label: string) => translateDashboardDistributionLabel(label, t);
  const nonZero = data.filter((d) => d.value > 0);
  const showPie = data.length >= 2;
  const hasSingle = nonZero.length === 1 && !showPie;

  const cardSx = {
    p: 2.5,
    borderRadius: 2,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    bgcolor: "background.paper",
    border: "1px solid",
    borderColor: "divider",
    height: "100%",
    display: "flex",
    flexDirection: "column" as const,
    minHeight: 120
  };

  if (data.length === 0 || nonZero.length === 0) {
    return (
      <Box sx={{ ...cardSx }}>
        {title && (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            fontWeight={600}
            sx={{ mb: 0.5 }}
          >
            {title}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          {resolvedEmpty}
        </Typography>
      </Box>
    );
  }

  if (hasSingle) {
    return (
      <Box sx={{ ...cardSx, justifyContent: "space-between" }}>
        {title && (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            fontWeight={600}
            sx={{ mb: 0.5 }}
          >
            {title}
          </Typography>
        )}
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {nonZero[0].value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {dist(nonZero[0].label)}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: 200,
        ...cardSx
      }}
    >
      {title && (
        <Typography
          variant="subtitle2"
          color="text.secondary"
          fontWeight={600}
          sx={{ mb: 1 }}
        >
          {title}
        </Typography>
      )}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          p: 0.5
        }}
      >
        <PieChart
          height={220}
          series={[
            {
              innerRadius: 40,
              outerRadius: 80,
              paddingAngle: 2,
              cornerRadius: 4,
              arcLabel: (item) => (item.value > 0 ? String(item.value) : ""),
              data: data.map((d, i) => ({
                id: i,
                value: d.value,
                label: dist(d.label)
              }))
            }
          ]}
          slotProps={{
            legend: {
              direction: "horizontal",
              position: { vertical: "bottom", horizontal: "center" }
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default DistributionPieChart;
